import { useState } from "react";
import { DateTime } from "luxon";
import { MonthPicker } from "@/components/ui/month-picker";
import { EngagementChart } from "./engagement-chart";
import { MonthlyBonus } from "./monthly-bonus";
import { RetentionChart } from "./retention-chart";
import { MatchRate } from "./match-rate";
import { UserTable } from "./users-table/user-table";
import {
  useGetMentorEngagementData,
  useGetMentorIncentiveData,
  useGetMentorRetentionData,
  useGetMentorProfileInterestData,
} from "@/lib/api/mentorships";
import { useSearch } from "@tanstack/react-router";
import { useCurrentUser, UserRole, useUser } from "@/lib/api/user";
import { useTrackMentorActiveTeens } from "@/lib/posthog-events";
import { cn } from "@/lib/utils";

interface MentorDataDashboardProps {
  mentorId?: string;
  embedded?: boolean;
}

export function MentorDataDashboard({
  mentorId: mentorIdProp,
  embedded = false,
}: MentorDataDashboardProps = {}) {
  const { mentorId } = useSearch({ strict: false }) as { mentorId?: string };
  const { data: currentUser } = useCurrentUser();
  const targetId = mentorIdProp || mentorId || currentUser?.id || "";

  const currentDate = DateTime.now();
  const currentMonth = currentDate.month - 1;
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const normalizedMonth = selectedMonth + 1;
  const monthLabel = DateTime.now()
    .set({ month: selectedMonth + 1 })
    .toFormat("MMMM yyyy");

  const { data: targetUser } = useUser(embedded ? "" : targetId);

  const { data: engagementData, isPending: isMentorEngagementDataLoading } =
    useGetMentorEngagementData(targetId, normalizedMonth);
  const { data: retentionData, isPending: isMentorRetentionDataLoading } =
    useGetMentorRetentionData(targetId, normalizedMonth);
  const { data: incentiveData, isPending: isMentorIncentiveDataLoading } =
    useGetMentorIncentiveData(targetId, normalizedMonth);
  const {
    data: profileInterestData,
    isPending: isMentorProfileInterestDataLoading,
  } = useGetMentorProfileInterestData(targetId, normalizedMonth);

  useTrackMentorActiveTeens(
    engagementData?.data?.engagementData?.engagementData?.[0]?.active_teens,
  );

  const title =
    currentUser?.role !== UserRole.MENTOR && targetUser
      ? `${targetUser?.name}'s Dashboard`
      : "Welcome back!";

  return (
    <div className={cn(!embedded && "px-[80px] py-[36px]")}>
      <div
        className={cn(
          "mb-8 flex gap-4 md:items-center",
          embedded ? "justify-end" : "flex-col justify-between md:flex-row",
        )}
      >
        {embedded ? null : (
          <div className="mb-[32px] h-[60px]">
            <h1 className="font-avantt text-2xl font-bold leading-8 tracking-[-0.48px] text-[#1C1C1C] md:text-3xl">
              {title}
            </h1>
            <p className="font-twk-lausanne mt-[4px] text-base font-normal leading-6 text-[#777]">
              {monthLabel}
            </p>
          </div>
        )}
        <div className={cn(embedded && "ml-auto")}>
          <MonthPicker
            month={selectedMonth}
            setMonth={setSelectedMonth}
            monthsToShow={6}
            showYear={true}
            className="w-[148px] px-[12px] py-[8px] text-center"
          />
        </div>
      </div>

      <div className="mb-[40px] grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <EngagementChart
            engagementData={engagementData}
            isLoading={isMentorEngagementDataLoading}
          />
        </div>

        <div className={cn("space-y-6", !embedded && "h-[710px]")}>
          <MonthlyBonus
            incentiveData={incentiveData?.data?.incentiveData}
            isLoading={isMentorIncentiveDataLoading}
            embedded={embedded}
          />
          <RetentionChart
            retentionData={retentionData}
            isLoading={isMentorRetentionDataLoading}
          />
          <MatchRate
            profileInterestData={profileInterestData}
            isLoading={isMentorProfileInterestDataLoading}
          />
        </div>
      </div>

      <UserTable
        selectedMonth={normalizedMonth}
        defaultPageSize={30}
        mentorId={targetId}
      />
    </div>
  );
}
